import * as React from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { atom, useAtomValue } from 'jotai';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';

import { WorkerCertificate } from '@restworld/utility-types';
import { FormLabelWrapper, HelperTextRElement, TextInput } from '@restworld/ui-ds-base';
import { DatePicker } from '@restworld/ui-ds-base';
import { genericDateFormat, toDateFormat } from '@restworld/utils-common';

import {
  EditFieldOptionsModal,
  EditModalFooter,
  EditModalTitle,
  FieldOptionsViewType,
  IEditModalProps,
} from '../profile-edit-modal';
import { getWorkerAtom, getWorkerFieldOptionsAtom } from '../../../global-state.worker-data';
import { useApiSubmitHandler } from '../../hooks';

type ICertificationsFormContentProps = IEditModalProps;
type InputProps = {
  certificate_type: string;
  course_name: string;
  corporate_name: string;
  date: string;
};
const defaultValuesAtom = atom((get) => {
  const { data: workerData } = get(getWorkerAtom);
  return workerData.certificate.map((v) => ({
    certificate_type: v.certificate_type,
    course_name: v.course_name,
    corporate_name: v.corporate_name,
    date: v.date,
  }));
});
export const CertificationsFormContent: React.FunctionComponent<ICertificationsFormContentProps> = ({
  onClose,
  editPageTitle,
  mode,
  editIndex,
}) => {
  const { t } = useTranslation();
  const defaultValues = useAtomValue(defaultValuesAtom)[editIndex];
  const { data: workerFieldOptionsData } = useAtomValue(getWorkerFieldOptionsAtom);
  const { data: workerData } = useAtomValue(getWorkerAtom);
  const {
    handleSubmit,
    control,
    getValues,
    setValue,
    reset,
    formState: { errors },
  } = useForm<InputProps>({
    reValidateMode: 'onSubmit',
    defaultValues: mode === 'edit' ? defaultValues : {},
  });
  const { apiSubmitHandler, loading } = useApiSubmitHandler({ onClose, editIndex, editPageType: 'certifications' });
  const { fieldOptionsView, toggleFieldOptionsView } = useFieldOptions();
  const onSubmit: SubmitHandler<InputProps> = React.useCallback(
    (params) => {
      try {
        const newCertificate = {
          ...params,
          ...(params.date ? { date: toDateFormat(params.date, genericDateFormat) } : {}),
        };
        if (mode === 'add') {
          apiSubmitHandler({
            certificate: [
              ...(workerData?.certificate ? workerData.certificate : []),
              ...[newCertificate],
            ] as WorkerCertificate[],
          });
        } else {
          const educations = [...workerData.certificate];
          educations[editIndex] = newCertificate as WorkerCertificate;
          apiSubmitHandler({ certificate: educations as WorkerCertificate[] });
        }
      } catch (error) {
        console.error(error, `Error in education form submit`);
      }
    },
    [apiSubmitHandler, editIndex, mode, workerData.certificate]
  );
  const checkIfErrored = (fieldKey: keyof InputProps) => errors && Object.keys(errors).includes(fieldKey);
  // const fieldLanguages = workerData.certificate.map((v) => v.education_title);
  // const selectedEducation = defaultValues?.education_title;
  // const selectedEducations = fieldLanguages?.filter((v) => v !== selectedEducation);
  // const educationOptions = [...workerFieldOptionsData.educationTitle].filter((v) => !selectedEducations.includes(v.id));
  return (
    <div className="w-full">
      <EditModalTitle
        onClose={onClose}
        editPageTitle={editPageTitle}
        onDelete={
          mode === 'edit'
            ? () => {
                const inputProps = {
                  certificate: [...workerData.certificate].filter((_, i) => i !== editIndex),
                };
                apiSubmitHandler(inputProps);
              }
            : null
        }
        loading={loading}
      />
      <form action="" method="post" onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <FormLabelWrapper id={`certificate_type`} label={t('onboarding-page:type_of_certificate')}>
          <Controller
            name={`certificate_type`}
            control={control}
            rules={{ required: mode === 'add' }}
            render={({ field }) => (
              <TextInput
                id={`certificate_type`}
                placeholder={t('onboarding-page:choose_title_type')}
                type="text"
                rightIcon={ChevronDownIcon}
                helperText={
                  checkIfErrored('certificate_type') ? (
                    <HelperTextRElement helperText={t('onboarding-page:this_field_is_required')} />
                  ) : null
                }
                color={checkIfErrored('certificate_type') ? 'failure' : undefined}
                readOnly
                {...field}
                value={workerFieldOptionsData?.certificateType?.find((v) => v.id === field.value)?.label || ''}
                onClick={() => {
                  toggleFieldOptionsView({
                    showOptionsView: true,
                    id: `certificate_type`,
                    selected: field.value ?? '',
                    options: workerFieldOptionsData?.certificateType,
                    mainTitle: t('onboarding-page:type_of_certificate'),
                  });
                }}
              />
            )}
          />
        </FormLabelWrapper>
        <FormLabelWrapper isFullWidth id="course_name" label={t('onboarding-page:course_name')}>
          <Controller
            name="course_name"
            control={control}
            rules={{ required: false }}
            render={({ field }) => (
              <TextInput
                id="course_name"
                placeholder={t('onboarding-page:enter_the_course_name')}
                type="text"
                helperText={
                  <HelperTextRElement
                    helperText={
                      Object.keys(errors).includes('course_name') ? t('onboarding-page:this_field_is_required') : ``
                    }
                  />
                }
                color={Object.keys(errors).includes('course_name') ? 'failure' : undefined}
                {...field}
              />
            )}
          />
        </FormLabelWrapper>
        <FormLabelWrapper id="date" label={t('common:release_date')}>
          <Controller
            name="date"
            control={control}
            rules={{ required: mode === 'add' }}
            render={({ field }) => {
              return (
                <DatePicker
                  id="date"
                  selectRange={false}
                  helperText={checkIfErrored('date') ? t('onboarding-page:this_field_is_required') : null}
                  fieldState={checkIfErrored('date') ? 'error' : 'normal'}
                  locale="it-IT"
                  maxDate={new Date()}
                  {...field}
                  {...(field.value ? { value: new Date(field.value) } : {})}
                />
              );
            }}
          />
        </FormLabelWrapper>
        <FormLabelWrapper isFullWidth id="corporate_name" label={t('onboarding-page:issue_organization')}>
          <Controller
            name="corporate_name"
            control={control}
            rules={{ required: false }}
            render={({ field }) => (
              <TextInput
                id="corporate_name"
                placeholder={t('onboarding-page:who_issued_the_course')}
                type="text"
                helperText={
                  <HelperTextRElement
                    helperText={
                      Object.keys(errors).includes('corporate_name') ? t('onboarding-page:this_field_is_required') : ``
                    }
                  />
                }
                color={Object.keys(errors).includes('corporate_name') ? 'failure' : undefined}
                {...field}
              />
            )}
          />
        </FormLabelWrapper>
        <EditModalFooter
          onClose={() => {
            onClose();
            reset(defaultValues);
          }}
          loading={loading}
          editIndex={editIndex}
          editPageType="certifications"
        />
      </form>
      <EditFieldOptionsModal
        {...fieldOptionsView}
        value={fieldOptionsView.selected ?? (getValues(fieldOptionsView.id) as string[] | string)}
        onChange={(value) => {
          if (typeof value === 'string') {
            toggleFieldOptionsView((p) => ({ ...p, selected: value }));
          }
        }}
        onBack={() => toggleFieldOptionsView(defaultModalFieldOptions)}
        onSave={() => {
          setValue(fieldOptionsView.id, fieldOptionsView.selected);
          toggleFieldOptionsView(defaultModalFieldOptions);
        }}
        visible={fieldOptionsView.showOptionsView}
        returnType="string"
      />
    </div>
  );
};

export default CertificationsFormContent;
interface FieldOptionsModalViewType extends Omit<FieldOptionsViewType, 'id'> {
  id: 'certificate_type';
  selected: string;
}
const defaultFieldOptions: FieldOptionsModalViewType = {
  showOptionsView: false,
  options: [],
  mainTitle: '',
  subTitle: '',
  id: 'certificate_type',
  returnType: 'string',
  selected: '',
};
const defaultModalFieldOptions = {
  ...defaultFieldOptions,
  id: 'certificate_type',
} as FieldOptionsModalViewType;
const useFieldOptions = () => {
  const [fieldOptionsView, toggleFieldOptionsView] = React.useState<FieldOptionsModalViewType | null>({
    ...defaultModalFieldOptions,
  });
  return {
    fieldOptionsView,
    toggleFieldOptionsView,
  };
};
